/* =====================================================================================
   Pipeline and Channel seed data.

   Shapes match the SQL views these screens will read in production. Content is
   illustrative except where it derives from the verified research pass, which is marked.
   ===================================================================================== */

import { demo } from './demo';


export * from './commercial.config';
import type { Opportunity, Partner, Tender } from './commercial.config';
export const OPPORTUNITIES: Opportunity[] = demo<Opportunity>([
  {
    id: 'o1', ref: 'OPP-00012',
    name: 'NWLP NGS HLA typing framework',
    demandAccount: 'Hammersmith Hospital, Imperial College Healthcare',
    buyingAccount: 'North West London Pathology',
    procurementAccount: 'Imperial College Healthcare NHS Trust',
    siteId: 'gbr-hammersmith', country: 'GBR',
    stage: 'procurement', saleType: 'tender',
    amount: 3_000_000, currency: 'GBP', recurringAnnual: 650_000,
    expectedClose: '2026-11-01',
    estimatedTestsYr: 9000, pricePerTest: 72,
    competitor: 'One Lambda', incumbentContractEnd: '2026-10-31',
    blockedOnReimbursement: false,
    nextStep: 'Register interest ahead of the tender notice',
    nextStepDue: '2026-09-01',
    owner: 'Dr Lawrence', daysInStage: 12,
    note:
      'VERIFIED from Find a Tender notice 2026/S 000-052240. Current reagent spend £600-700k a year, ' +
      'projected £3.0-3.5M over five years, contract Nov 2026 to Oct 2031 extendable to 2033. ' +
      'Explicitly flagged as suitable for SMEs. Market engagement closed 3 July 2026.',
  },
  {
    id: 'o2', ref: 'OPP-00014',
    name: 'Padova dd-cfDNA surveillance evaluation',
    demandAccount: 'Azienda Ospedale-Università Padova',
    buyingAccount: 'Azienda Ospedale-Università Padova',
    payerAccount: 'Regione Veneto',
    siteId: 'ita-padova', country: 'ITA',
    stage: 'evaluation', saleType: 'direct',
    amount: 420_000, currency: 'EUR', recurringAnnual: 420_000,
    expectedClose: '2027-03-31',
    estimatedTestsYr: 1400, pricePerTest: 300,
    champion: 'Transplant nephrology lead',
    blockedOnReimbursement: true, reimbursementRoute: 'LEA listing then Veneto regional tariff',
    nextStep: 'Agree evaluation protocol and sample volumes',
    nextStepDue: '2026-09-15',
    owner: 'Dr Lawrence', daysInStage: 38,
    note:
      'Highest-value Italian account. Largest living-donor kidney programme in Italy, largest ' +
      'paediatric cohort, and already invested in digital pre-implantation biopsy. Blocked on ' +
      'regional funding rather than on clinical interest.',
  },
  {
    id: 'o3', ref: 'OPP-00015',
    name: 'Bari urinary biomarker collaboration',
    demandAccount: 'AOU Consorziale Policlinico di Bari',
    buyingAccount: 'AOU Consorziale Policlinico di Bari',
    siteId: 'ita-bari', country: 'ITA',
    stage: 'qualified', saleType: 'trial',
    amount: 85_000, currency: 'EUR',
    expectedClose: '2027-01-31',
    blockedOnReimbursement: false,
    nextStep: 'Scope joint protocol against their CXCL10 work',
    nextStepDue: '2026-09-30',
    owner: 'Dr Lawrence', daysInStage: 9,
    note:
      'The Bari consortium already published urinary CXCL10 and FasL in subclinical rejection. ' +
      'Treat as both a competitive signal and the strongest available collaboration for the CXCL9 ' +
      'point-of-care assay.',
  },
  {
    id: 'o4', ref: 'OPP-00009',
    name: "L'Aquila HLA reference laboratory evaluation",
    demandAccount: 'Ospedale Civile S. Salvatore',
    buyingAccount: 'Centro Regionale Immunoematologia e Tipizzazione Tissutale, ASL 1 Abruzzo',
    siteId: 'ita-laquila', country: 'ITA',
    stage: 'qualified', saleType: 'direct',
    amount: 140_000, currency: 'EUR', recurringAnnual: 140_000,
    expectedClose: '2027-06-30',
    blockedOnReimbursement: false,
    nextStep: 'Introductory meeting with the AIBT president',
    nextStepDue: '2026-09-08',
    owner: 'Dr Lawrence', daysInStage: 21,
    note:
      'Modest transplant volume but the seat of the AIBT presidency and a national reference ' +
      'immunogenetics laboratory. This is a credibility account, not a volume account.',
  },
  {
    id: 'o5', ref: 'OPP-00021',
    name: 'Bologna S. Orsola rejection surveillance',
    demandAccount: 'IRCCS AOU di Bologna, Policlinico S. Orsola',
    buyingAccount: 'IRCCS AOU di Bologna',
    payerAccount: 'Regione Emilia-Romagna',
    siteId: 'ita-bologna', country: 'ITA',
    stage: 'identified', saleType: 'direct',
    amount: 380_000, currency: 'EUR',
    expectedClose: '2027-09-30',
    estimatedTestsYr: 1250, pricePerTest: 300,
    blockedOnReimbursement: true, reimbursementRoute: 'Emilia-Romagna regional tariff',
    nextStep: 'Identify clinical champion',
    nextStepDue: '2026-10-15',
    owner: 'Dr Lawrence', daysInStage: 4,
    note: 'Largest DCD-listed cohort in Italy (509) and second-largest hypersensitised (32).',
  },
  {
    id: 'o6', ref: 'OPP-00018',
    name: 'Edinburgh in-house validation, Regulation 33 route',
    demandAccount: 'Royal Infirmary of Edinburgh',
    buyingAccount: 'SNBTS Histocompatibility and Immunogenetics',
    payerAccount: 'NHS National Services Division',
    siteId: 'gbr-edinburgh', country: 'GBR',
    stage: 'business_case', saleType: 'service_lab',
    amount: 190_000, currency: 'GBP', recurringAnnual: 190_000,
    expectedClose: '2027-04-30',
    blockedOnReimbursement: false,
    nextStep: 'Prepare NSD designation registration',
    nextStepDue: '2027-02-28',
    owner: 'Dr Lawrence', daysInStage: 27,
    note:
      'Uses the unconditional GB health institution exemption (UK MDR 2002 reg. 33(1)(a)) so no ' +
      'UKCA or CE mark is required. Scotland is the fastest formal route in the UK but the NSD ' +
      'registration deadline of 28 February is rigid.',
  },
  {
    id: 'o7', ref: 'OPP-00023',
    name: 'KFSHRC Riyadh HLA typing, via distributor',
    demandAccount: 'King Faisal Specialist Hospital & Research Centre',
    buyingAccount: 'King Faisal Specialist Hospital & Research Centre',
    procurementAccount: 'NUPCO',
    country: 'SAU',
    stage: 'identified', saleType: 'distributor', partner: 'Gulf Medical Systems (prospective)',
    amount: 260_000, currency: 'USD',
    expectedClose: '2027-12-31',
    blockedOnReimbursement: false,
    nextStep: 'Complete distributor due diligence before any commitment',
    nextStepDue: '2026-11-30',
    owner: 'Dr Lawrence', daysInStage: 2, slipped: true,
    note:
      'Blocked at order entry until anti-bribery due diligence is approved. SFDA authorisation is ' +
      'held in the agent name and is difficult to move later, so distributor choice is close to ' +
      'irreversible. Do not rush it.',
  },
  {
    id: 'o8', ref: 'OPP-00007',
    name: 'Manchester MFT typing displacement',
    demandAccount: 'Manchester Royal Infirmary',
    buyingAccount: 'Transplantation Laboratory, Manchester University NHS FT',
    siteId: 'gbr-manchester', country: 'GBR',
    stage: 'lost', saleType: 'direct',
    amount: 220_000, currency: 'GBP',
    expectedClose: '2026-06-30',
    competitor: 'One Lambda',
    blockedOnReimbursement: false,
    nextStep: 'Revisit at contract renewal',
    nextStepDue: '2028-04-01',
    owner: 'Dr Lawrence', daysInStage: 62,
    note: 'Lost on incumbent lock-in rather than on price or performance. Renewal is the re-entry point.',
  },
]);

/* --- Channel ---------------------------------------------------------------------- */


export const PARTNERS: Partner[] = demo<Partner>([
  {
    id: 'p1', name: 'Gulf Medical Systems', country: 'SAU', type: 'distributor',
    status: 'due_diligence',
    territories: [{ country: 'SAU', product: 'Scira HLA', exclusivity: 'exclusive', until: '2029-12-31' }],
    commitmentValue: 400_000, commitmentCurrency: 'USD', achievedPct: 0,
    dueDiligence: {
      status: 'in_progress', riskRating: 'high', nextReview: '2026-11-30',
      sanctionsScreened: true, pepScreened: false, abacSigned: false,
    },
    regulatoryRole: 'Prospective SFDA Authorised Representative',
    note:
      'BLOCKING: orders cannot be raised until due diligence is approved. High risk rating because ' +
      'the distributor sells into government hospitals, which is exactly the UK Bribery Act s.7 ' +
      'exposure. PEP screening on beneficial owners is outstanding.',
  },
  {
    id: 'p2', name: 'Iberia Diagnostica SL', country: 'ESP', type: 'distributor',
    status: 'contracted',
    territories: [
      { country: 'ESP', product: 'Scira HLA', exclusivity: 'exclusive', until: '2028-06-30' },
      { country: 'PRT', product: 'Scira HLA', exclusivity: 'non_exclusive', until: '2028-06-30' },
    ],
    commitmentValue: 250_000, commitmentCurrency: 'EUR', achievedPct: 62,
    dueDiligence: {
      status: 'approved', riskRating: 'low', nextReview: '2027-03-15',
      sanctionsScreened: true, pepScreened: true, abacSigned: true,
    },
    regulatoryRole: 'EU importer',
    sellThroughDue: '2026-10-15', sellThroughStatus: 'due',
    note: 'Tracking at 62% of the annual minimum with five months to run. Watch: falling short converts exclusivity to non-exclusive.',
  },
  {
    id: 'p3', name: 'Emirates Lab Partners FZ-LLC', country: 'ARE', type: 'agent',
    status: 'prospective',
    territories: [],
    dueDiligence: {
      status: 'none', sanctionsScreened: false, pepScreened: false, abacSigned: false,
    },
    note: 'No territory granted and no diligence started. Do not discuss exclusivity before diligence.',
  },
  {
    id: 'p4', name: 'Nordisk Diagnostik AB', country: 'SWE', type: 'distributor',
    status: 'active',
    territories: [
      { country: 'SWE', product: 'Scira HLA', exclusivity: 'exclusive', until: '2027-12-31' },
      { country: 'NOR', product: 'Scira HLA', exclusivity: 'exclusive', until: '2027-12-31' },
      { country: 'DNK', product: 'Scira HLA', exclusivity: 'semi_exclusive', until: '2027-12-31' },
    ],
    commitmentValue: 180_000, commitmentCurrency: 'EUR', achievedPct: 104,
    dueDiligence: {
      status: 'approved', riskRating: 'low', nextReview: '2027-01-20',
      sanctionsScreened: true, pepScreened: true, abacSigned: true,
    },
    regulatoryRole: 'EU importer',
    sellThroughDue: '2026-09-30', sellThroughStatus: 'submitted',
    note: 'Only partner exceeding commitment. Nordic sales are a health-economics case to regions, not a tariff argument.',
  },
]);


export const TENDERS: Tender[] = demo<Tender>([
  {
    id: 't1', ref: '2026/S 000-052240',
    title: 'NGS HLA Typing Systems and Reagents',
    authority: 'Imperial College Healthcare NHS Trust (North West London Pathology)',
    country: 'GBR',
    estimatedValue: 3_000_000, currency: 'GBP',
    published: '2026-06-03', submissionDeadline: '2026-09-01',
    contractStart: '2026-11-01', contractMonths: 60,
    bidDecision: 'bid', status: 'qualifying',
    note:
      'VERIFIED. Preliminary market engagement closed 3 July 2026; tender notice expected 1 September. ' +
      'States that high-resolution NGS HLA typing is a mandatory clinical requirement and the ' +
      'international standard of care. Explicitly flagged as suitable for SMEs.',
  },
  {
    id: 't2', ref: 'NHSBT1249-R',
    title: 'HLA-specific antibody detection and definition systems (renewal expected)',
    authority: 'NHS Blood and Transplant',
    country: 'GBR',
    estimatedValue: 7_143_000, currency: 'GBP',
    submissionDeadline: '2027-02-28',
    bidDecision: 'undecided', status: 'identified',
    note:
      'The 2021 award to Immucor and VH Bio was a four-year framework, so renewal is due. It covers ' +
      'NHSBT plus SNBTS plus the Welsh Blood Service, so one award effectively sets the antibody ' +
      'platform for three nations. Date is estimated, not published.',
  },
  {
    id: 't3', ref: 'NUPCO-2027-LAB-004',
    title: 'Histocompatibility reagents, national framework',
    authority: 'NUPCO',
    country: 'SAU',
    estimatedValue: 1_800_000, currency: 'USD',
    submissionDeadline: '2027-03-31',
    bidDecision: 'undecided', status: 'identified',
    partner: 'Gulf Medical Systems (prospective)',
    note: 'Cannot bid without SFDA authorisation and a registered local agent. Both are long lead-time items.',
  },
]);
